import { Autocomplete, TextField, Typography } from "@mui/material";
import { isEmpty } from "lodash-es";
import { type FC, type SyntheticEvent, useCallback, useMemo } from "react";

import { GreyBox } from "../../components/grey-box";
import type { DynamicSegmentRecipientData } from "../../hooks/use-dynamic-segment-recipients";
import { usePreviewContext } from "../../preview-context";

export type SelectedDynamicTargeting =
  | { type: "MOMENT_SEGMENT"; data?: undefined }
  | { type: "dynamic_segment"; data: { id: string; info: DynamicSegmentRecipientData[string] } };

export const PreviewWithSidebarTargetingSegment: FC<{
  selected: SelectedDynamicTargeting;
  onChange: (value: SelectedDynamicTargeting) => void;
}> = ({ selected, onChange }) => {
  const {
    moment_preview_data: { segment },
  } = usePreviewContext();

  const segment_name = segment?.name ?? "Ad-Hoc Communication segment";

  const {
    preview_info: { loading, data },
  } = usePreviewContext();

  const momentSegmentOption = useMemo(
    () => ({
      label: segment_name,
      value: "MOMENT_SEGMENT",
    }),
    [segment_name],
  );

  const options = useMemo(() => {
    if (loading) return [];

    const opts = Object.entries(data?.dynamicSegmentRecipients ?? {})
      .map(([key, value]) => ({
        label: value.name ?? `Ad-Hoc Dynamic Segment (${key})`,
        value: key,
      }))
      .filter((o) => o.label !== segment_name);

    return [momentSegmentOption, ...opts];
  }, [loading, data, momentSegmentOption, segment_name]);

  const value = useMemo(() => {
    if (selected.type === "MOMENT_SEGMENT") return momentSegmentOption;

    return options.find((o) => o.value === selected.data.id) ?? momentSegmentOption;
  }, [selected, momentSegmentOption, options]);

  const handleChange = useCallback(
    (_: SyntheticEvent, newVal: { value: string; label: string } | null) => {
      if (loading || !data) return null;

      if (newVal?.value === "MOMENT_SEGMENT" || !newVal?.value) {
        onChange({ type: "MOMENT_SEGMENT" });
        return;
      }

      const selectedSegment = data.dynamicSegmentRecipients[newVal.value];
      if (!selectedSegment) return;

      onChange({ type: "dynamic_segment", data: { id: newVal.value, info: selectedSegment } });
    },
    [loading, data, onChange],
  );

  if (isEmpty(data?.dynamicSegmentRecipients)) return null;

  if (!loading && options.length === 0)
    return (
      <GreyBox>
        <Typography variant="body2" color="textSecondary">
          Previewing with Test data - nobody is currently in the recipient segment
        </Typography>
      </GreyBox>
    );

  return (
    <Autocomplete
      disableClearable
      loading={loading}
      options={options}
      getOptionLabel={(option) => option.label}
      onChange={handleChange}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      value={value}
      renderInput={(params) => (
        <TextField {...params} label="Filter by segment" variant="outlined" />
      )}
    />
  );
};
